import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  LinearProgress,
  CircularProgress,
  IconButton,
  Tooltip as MuiTooltip,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import {
  LocalShipping,
  People,
  Article,
  AttachMoney,
  DirectionsBoat,
  TrendingUp,
  Schedule,
  NotificationsActive,
  CheckCircle,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import { selectCurrentUser } from "../../redux/slices/authSlice";
import { selectUsdToCiRate } from "../../redux/slices/systemSettingsSlice";
import { ciToUsd, formatUsd } from "../../utils/currency";
import api from "../../api/axiosInstance";
import KpiCard from "../../components/common/KpiCard";
import StatusChip from "../../components/common/StatusChip";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";

const truncateWords = (text, maxWords) => {
  if (!text) return "—";
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}...`;
};

// Same short port codes as AdminSailing.jsx's sailing list row — the full
// "PORT EVERGLADES, FLA → GEORGE TOWN, GRAND CAYMAN" style route takes up
// too much space here too. Falls back to the first 3 letters of the port
// name for anything not in this map.
const PORT_CODES = {
  "port everglades, fl": "USPEF",
  "port everglades, fla": "USPEF",
  "george town, cayman islands": "KYGEC",
  "george town, grand cayman": "KYGEC",
};
const portCode = (port) => {
  if (!port) return "—";
  return PORT_CODES[port.trim().toLowerCase()] || port.trim().slice(0, 3).toUpperCase();
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const user = useSelector(selectCurrentUser);
  const usdToCiRate = useSelector(selectUsdToCiRate);
  const { enqueueSnackbar } = useSnackbar();

  const [stats, setStats] = useState(null);
  const [recentShipments, setRecentShipments] = useState([]);
  const [sailings, setSailings] = useState([]);
  const [finSummary, setFinSummary] = useState(null);
  const [netProfit, setNetProfit] = useState(0);
  // Real sailings (excludes the "unassigned" bucket — see
  // reportController.getProfitLoss) currently running a loss, so it's
  // surfaced here rather than only being discoverable by opening Reports.
  const [lossSailingsCount, setLossSailingsCount] = useState(0);
  const [customsActions, setCustomsActions] = useState([]);
  const [cutoffs, setCutoffs] = useState([]);
  const [cutoffPendingCount, setCutoffPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [remindingId, setRemindingId] = useState(null);

  const handleSendReminder = async (e, shipment) => {
    e.stopPropagation(); // don't also trigger the row's navigate-to-shipment
    setRemindingId(shipment._id);
    try {
      const { data } = await api.post(`/shipments/${shipment._id}/send-invoice-reminder`);
      enqueueSnackbar(data.message || "Reminder sent", { variant: "success" });
      setCustomsActions((prev) =>
        prev.map((s) => (s._id === shipment._id ? { ...s, invoiceReminderSentAt: new Date().toISOString() } : s)),
      );
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to send reminder", { variant: "error" });
    } finally {
      setRemindingId(null);
    }
  };

  // Ticks every second so the cutoff countdown reads as a live timer —
  // the underlying deadline only changes if an admin edits it in Settings,
  // but "time remaining" itself is always moving.
  const [now, setNow] = useState(() => dayjs());
  useEffect(() => {
    const timer = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [statsRes, shipmentsRes, sailingsRes, finRes, custRes, cutoffRes, plRes] =
          await Promise.all([
            api.get("/shipments/stats"),
            api.get("/shipments", {
              params: { limit: 5, sortBy: "createdAt", sortOrder: "desc" },
            }),
            api.get("/sailing"),
            api.get("/invoices/summary"),
            // Every shipment with no purchase invoice uploaded yet —
            // deliberately NOT status:"awaiting_invoice", which misses any
            // shipment that already moved on to in_transit (or beyond)
            // without ever getting an invoice (invoice isn't a hard gate on
            // sailing assignment, only on customs-clearance item transitions).
            api.get("/shipments", {
              params: { missingInvoice: "true", limit: 5 },
            }),
            api.get("/sailing/cutoff-status"),
            // Same report Reports > Profit & Loss uses — CI$, converted
            // below like every other money figure on this page.
            api.get("/reports/profit-loss"),
          ]);
        setStats(statsRes.data.data);
        setRecentShipments(shipmentsRes.data.data || []);
        // "Sailing status" highlights — every sailing that hasn't completed
        // yet, soonest departure first, so an admin can see at a glance
        // which sailings are Scheduled/Loading/Sailing/Arrived.
        setSailings(
          (sailingsRes.data.data?.schedules || [])
            .filter((s) => s.status !== "completed")
            .sort((a, b) => new Date(a.departureDate) - new Date(b.departureDate))
            .slice(0, 5),
        );
        setFinSummary(finRes.data.data);
        setCustomsActions(custRes.data.data || []);
        setCutoffs(cutoffRes.data.data?.cutoffs || []);
        setCutoffPendingCount(cutoffRes.data.data?.pendingCount || 0);
        setNetProfit(plRes.data.data?.totals?.profit || 0);
        // Server-computed from the FULL dataset (reportController.getProfitLoss),
        // not from `sailings` — that array is now paginated (By Sailing table
        // on the Reports page), so it no longer reflects every sailing.
        setLossSailingsCount(plRes.data.data?.totals?.lossSailingsCount || 0);
      } catch (e) {
        console.error("Dashboard load error:", e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Full "Xd Xh Xm Xs" live timer.
  const formatCountdown = (targetDate) => {
    const diffMs = dayjs(targetDate).diff(now);
    if (diffMs <= 0) return "Cutoff passed";
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  const cutoffUrgency = (targetDate) => {
    const hoursLeft = dayjs(targetDate).diff(now, "hour", true);
    if (hoursLeft <= 24) return isDark ? { color: "#FC8181", bg: "#4A1C1C" } : { color: "#EF4444", bg: "#FEF2F2" };
    if (hoursLeft <= 72) return isDark ? { color: "#F6AD55", bg: "#4A2E12" } : { color: "#C05621", bg: "#FFFBEB" };
    return isDark ? { color: "#34D399", bg: "#0B3B2E" } : { color: "#0E9F6E", bg: "#ECFDF5" };
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const statusBreakdown = stats?.statusBreakdown || {};
  const tinBreakdown = stats?.tinBreakdown || [];
  const collectionRate = finSummary
    ? Math.round(
      ((finSummary.totalPaid || 0) /
        ((finSummary.totalPaid || 0) + (finSummary.outstanding || 1))) *
      100,
    )
    : 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title={`${greeting()}, ${user?.firstName} 👋`}
        subtitle={dayjs().format("dddd, D MMMM YYYY")}
        action={
          <Button
            variant="contained"
            startIcon={<TrendingUp />}
            onClick={() => navigate("/admin/reports")}
          >
            View Reports
          </Button>
        }
      />

      {/* KPIs */}
      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}
      >
        <KpiCard
          label="Total Shipments"
          value={loading ? "—" : (stats?.totalShipments ?? 0).toString()}
          icon={<LocalShipping />}
          iconBg="#EBF4FF"
          iconColor="#2B6CB0"
        />
        <KpiCard
          label="Awaiting Invoice"
          value={
            loading
              ? "—"
              : (statusBreakdown["awaiting_invoice"] ?? 0).toString()
          }
          deltaType="down"
          icon={<Article />}
          iconBg="#FFFBEB"
          iconColor="#C05621"
        />
        <KpiCard
          label="Active Customers"
          value={loading ? "—" : (stats?.activeCustomers ?? 0).toString()}
          icon={<People />}
          iconBg="#ECFDF5"
          iconColor="#0E9F6E"
        />
        <KpiCard
          label="Outstanding"
          value={loading ? "—" : formatUsd(ciToUsd(finSummary?.outstanding ?? 0, usdToCiRate))}
          deltaType={finSummary?.outstanding > 0 ? "down" : "neutral"}
          icon={<AttachMoney />}
          iconBg="#EBF4FF"
          iconColor="#2B6CB0"
        />
      </Box>

      {/* Surfaces a sailing running a loss right here instead of only being
          discoverable by opening Reports > Profit & Loss > By Sailing —
          same "warning banner" pattern as the cutoff-pending one below. */}
      {!loading && lossSailingsCount > 0 && (
        <Box
          onClick={() => navigate("/admin/reports")}
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: "error.light",
            cursor: "pointer",
            "&:hover": { opacity: 0.85 },
          }}
        >
          <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>
            ⚠️ {lossSailingsCount} sailing{lossSailingsCount === 1 ? "" : "s"} currently showing a loss — view
            Profit &amp; Loss by Sailing
          </Typography>
        </Box>
      )}

      {/* Next sailing cutoff countdown — only the nearest upcoming one
          (cutoffs is already sorted soonest-first by the backend). */}
      <Card>
        <CardHeader
          title={
            <Typography fontWeight={700} fontSize={14}>
              Next Sailing Cutoff
            </Typography>
          }
          subheader={
            <Typography variant="caption" color="text.secondary">
              When consolidation requests must be in by — editable in Settings
            </Typography>
          }
          action={
            <Button
              size="small"
              variant="outlined"
              startIcon={<Schedule sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/admin/settings")}
            >
              Edit
            </Button>
          }
          sx={{ pb: 0 }}
        />
        {loading ? (
          <SkeletonLoader rows={1} />
        ) : cutoffs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No cutoff rules configured yet.
          </Typography>
        ) : (
          (() => {
            const c = cutoffs[0];
            const urgency = cutoffUrgency(c.nextCutoffAt);
            return (
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: urgency.bg,
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 1.5,
                      bgcolor: "background.paper",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Schedule sx={{ color: urgency.color, fontSize: 26 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
                      {c.sailingDay} sailing — cutoff {c.cutoffDay} {c.cutoffTime}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 28,
                        fontWeight: 800,
                        color: urgency.color,
                        lineHeight: 1.3,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatCountdown(c.nextCutoffAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })()
        )}
        {!loading && cutoffPendingCount > 0 && (
          <Box
            onClick={() => navigate("/admin/ready-to-sail")}
            sx={{
              mx: 2,
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: "warning.light",
              cursor: "pointer",
              "&:hover": { opacity: 0.85 },
            }}
          >
            <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 600 }}>
              ⚠️ {cutoffPendingCount} received item(s) not yet assigned to a sailing or consolidation
            </Typography>
          </Box>
        )}
      </Card>

      {/* Main grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 340px" },
          gap: 2.5,
        }}
      >
        {/* Recent shipments */}
        <Card>
          <CardHeader
            title={
              <Typography fontWeight={700} fontSize={14}>
                Recent Shipments
              </Typography>
            }
            subheader={
              <Typography variant="caption" color="text.secondary">
                Latest entries
              </Typography>
            }
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate("/admin/shipments")}
              >
                View All
              </Button>
            }
            sx={{ pb: 0 }}
          />
          {loading ? (
            <SkeletonLoader rows={5} />
          ) : recentShipments.length === 0 ? (
            <EmptyState icon="📦" title="No shipments yet" />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tracking</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentShipments.map((s) => (
                  <TableRow
                    key={s._id}
                    onClick={() => navigate(`/admin/shipments/${s.trackingNumber}`)}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        color: "text.secondary",
                      }}
                    >
                      {s.trackingNumber}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <MuiTooltip title={`${s.customer?.firstName || ""} ${s.customer?.lastName || ""}`.trim()}>
                        <span>{truncateWords(`${s.customer?.firstName || ""} ${s.customer?.lastName || ""}`.trim(), 2)}</span>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      <MuiTooltip title={s.packageDescription || ""}>
                        <span>{truncateWords(s.packageDescription, 3)}</span>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={s.status} domain="shipment" />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatUsd(s.declaredValue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Right column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Upcoming sailings */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Sailing Status
                </Typography>
              }
              action={
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate("/admin/sailing")}
                >
                  Manage
                </Button>
              }
              sx={{ pb: 0 }}
            />
            {loading ? (
              <SkeletonLoader rows={3} />
            ) : sailings.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No upcoming sailings.
              </Typography>
            ) : (
              sailings.map((s) => (
                <Box
                  key={s._id}
                  onClick={() => navigate(`/admin/sailing/${s.sailingNumber}`)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 1.5,
                      bgcolor: "#001A3C",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <DirectionsBoat sx={{ color: "white", fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>
                        Sailing {s.sailingNumber} is
                      </Typography>
                      <StatusChip status={s.status} domain="sailing" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {s.vesselName} · {portCode(s.originPort)} → {portCode(s.destinationPort)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "primary.main",
                      flexShrink: 0,
                    }}
                  >
                    {dayjs(s.departureDate).format("MMM D")}
                  </Typography>
                </Box>
              ))
            )}
          </Card>

          {/* Finance snapshot */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Finance Snapshot
                </Typography>
              }
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1 }}>
              {loading ? (
                <SkeletonLoader rows={3} />
              ) : (
                <>
                  {[
                    {
                      label: "Outstanding",
                      value: formatUsd(ciToUsd(finSummary?.outstanding || 0, usdToCiRate)),
                      color: "error.main",
                    },
                    {
                      label: "Unpaid invoices",
                      value: finSummary?.outstandingCount || 0,
                      color: "warning.main",
                    },
                    {
                      label: "Total collected",
                      value: formatUsd(ciToUsd(finSummary?.totalPaid || 0, usdToCiRate)),
                      color: "success.main",
                    },
                    {
                      // Same figure as Reports > Profit & Loss's overall
                      // total — includes every sailing's Freight/MBL/HBOL/
                      // Port Fees, not just revenue minus Freight.
                      label: "Net profit",
                      value: formatUsd(ciToUsd(netProfit, usdToCiRate)),
                      color: netProfit >= 0 ? "success.main" : "error.main",
                    },
                  ].map((r) => (
                    <Box
                      key={r.label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.875,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {r.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={r.color}
                      >
                        {r.value}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{ mt: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      Collection rate
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(collectionRate, 100)}
                      sx={{
                        height: 6,
                        borderRadius: 99,
                        bgcolor: "divider",
                        "& .MuiLinearProgress-bar": { bgcolor: "#0E9F6E" },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#0E9F6E",
                        fontWeight: 700,
                        mt: 0.75,
                        display: "block",
                      }}
                    >
                      {collectionRate}% collected
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Bottom row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 2.5,
        }}
      >
        {/* Status breakdown */}
        <Card>
          <CardHeader
            title={
              <Typography fontWeight={700} fontSize={14}>
                Shipment Status Breakdown
              </Typography>
            }
            sx={{ pb: 0 }}
          />

          {loading ? (
            <SkeletonLoader rows={4} />
          ) : Object.keys(statusBreakdown).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No shipments yet.
            </Typography>
          ) : (
            <CardContent>
              {(() => {
                const COLORS = [
                  "#2563EB",
                  "#0E9F6E",
                  "#F59E0B",
                  "#EF4444",
                  "#8B5CF6",
                  "#14B8A6",
                  "#F97316",
                  "#EC4899",
                  "#64748B",
                  "#22C55E",
                ];

                const chartData = Object.entries(statusBreakdown).map(
                  ([status, count]) => ({
                    name: status
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase()),
                    value: count,
                    status,
                  }),
                );

                return (
                  <>
                    <Box sx={{ width: "100%", height: 300, paddingBottom: 3 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            innerRadius={45}
                            paddingAngle={3}
                            label={({ percent }) =>
                              `${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {chartData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>

                          <Tooltip />

                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            formatter={(value) => (
                              <span style={{ fontSize: 12 }}>{value}</span>
                            )}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* <Box mt={1}>
                      {chartData.map((item, index) => (
                        <Box
                          key={item.status}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            "&:last-child": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: COLORS[index % COLORS.length],
                              }}
                            />

                            <StatusChip status={item.status} />
                          </Box>

                          <Typography fontWeight={700}>{item.value}</Typography>
                        </Box>
                      ))}
                    </Box> */}
                  </>
                );
              })()}
            </CardContent>
          )}
        </Card>
        {/* Customs actions needed */}
        <Card>
          <CardHeader
            title={
              <Typography fontWeight={700} fontSize={14}>
                Awaiting Invoice / Action
              </Typography>
            }
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate("/admin/shipments")}
              >
                View All
              </Button>
            }
            sx={{ pb: 0 }}
          />
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : customsActions.length === 0 ? (
            <EmptyState icon="✅" title="No pending actions" />
          ) : (
            customsActions.map((s) => (
              <Box
                key={s._id}
                onClick={() => navigate(`/admin/shipments/${s.trackingNumber}`)}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-child": { borderBottom: "none" },
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "warning.light",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  ⚠️
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                    {s.trackingNumber} — Invoice required
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.customer?.firstName} {s.customer?.lastName} ·{" "}
                    {s.vendor?.name}
                  </Typography>
                </Box>
                {s.invoiceReminderSentAt ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }} title={`Reminded ${dayjs(s.invoiceReminderSentAt).format("MMM D, h:mma")}`}>
                    <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                  </Box>
                ) : (
                  <IconButton
                    size="small"
                    title="Send reminder to upload invoice"
                    onClick={(e) => handleSendReminder(e, s)}
                    disabled={remindingId === s._id}
                    sx={{ flexShrink: 0 }}
                  >
                    {remindingId === s._id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <NotificationsActive fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Box>
            ))
          )}
        </Card>
      </Box>

      {tinBreakdown.length > 0 && (
        <Card>
          <CardHeader
            title={
              <Typography fontWeight={700} fontSize={14}>
                Shipments by TIN
              </Typography>
            }
            sx={{ pb: 0 }}
          />
          {loading ? (
            <SkeletonLoader rows={3} />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TIN</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Shipments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tinBreakdown.map((row) => (
                  <TableRow key={row.tin}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {row.tin}
                      {row.label ? ` (${row.label})` : ""}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>
                      {row.sharedAcrossCustomers ? (
                        <Typography component="span" color="warning.main" fontWeight={600}>
                          {row.customerName} +{" more — shared across customers"}
                        </Typography>
                      ) : (
                        row.customerName || "—"
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {row.count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}
    </Box>
  );
};

export default AdminDashboard;
